import { ThemeEvents } from '@theme/events';

/**
 * <product-skardata>
 *
 * Skärdatatabellen på produktsidan. Metafältet lagrar bara verktygets
 * naturliga (okapade) chipload, varvtal och skärantal — matning räknas ut
 * här som chipload × skär × varvtal, och kapas mot 2 500 mm/min för
 * hobbyprofilen. Samma förenkling som Matningskollens kalkylator: ingen
 * automatisk sänkning av varvtalet om chiploaden hamnar under golvet, bara
 * ett kapat matningsvärde och en förklarande notis.
 *
 * Maskinväljaren delar localStorage-nyckel med Matningskollen
 * (v5-maskinprofil), så valet följer besökaren mellan sidorna.
 */
const LAGRING = 'v5-maskinprofil';
const HOBBYTAK = 2500; // mm/min — se machines.yaml: hobby.matning_tak

class ProductSkardata extends HTMLElement {
  connectedCallback() {
    this.maskinSelect = this.querySelector('[data-v5-maskin]');
    this.rpmInput = this.querySelector('[data-v5-rpm]');
    this.rpmAndrad = false;

    let sparad = null;
    try { sparad = localStorage.getItem(LAGRING); } catch (e) { /* privat läge */ }
    const profil = sparad === 'industriell' ? 'industriell' : 'hobby';
    this.dataset.profil = profil;
    if (this.maskinSelect) this.maskinSelect.value = profil;

    this.maskinSelect?.addEventListener('change', () => {
      this.dataset.profil = this.maskinSelect.value;
      try { localStorage.setItem(LAGRING, this.maskinSelect.value); } catch (e) { /* privat läge */ }
      this.rakna();
    });
    this.rpmInput?.addEventListener('input', () => {
      this.rpmAndrad = true;
      this.rakna();
    });

    this.rakna();

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.uppdatera);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.uppdatera);
  }

  /** Räknar om matning och chipload för alla rader utifrån valt varvtal och maskintyp. */
  rakna = () => {
    const body = this.querySelector('[ref="skardataBody"]');
    if (!body || !this.rpmInput) return;

    if (!this.rpmAndrad) this.rpmInput.value = body.dataset.rpmNative;
    const z = Number(body.dataset.z);
    const rpm = Number(this.rpmInput.value) || Number(body.dataset.rpmNative);
    const profil = this.dataset.profil;

    let nagonKapad = false;
    body.querySelectorAll('tbody tr').forEach((tr) => {
      const chipload = Number(tr.dataset.chipload);
      let feed = chipload * z * rpm;
      let visadChipload = chipload;
      let kapad = false;

      if (profil === 'hobby' && feed > HOBBYTAK) {
        feed = HOBBYTAK;
        visadChipload = feed / (z * rpm);
        kapad = true;
        nagonKapad = true;
      }

      const matningCell = tr.querySelector('[data-cell="matning"]');
      const chiploadCell = tr.querySelector('[data-cell="chipload"]');
      if (matningCell) matningCell.textContent = `${Math.round(feed)}${kapad ? ' *' : ''}`;
      if (chiploadCell) {
        const rundad = Math.round(visadChipload * 1000) / 1000;
        chiploadCell.textContent = String(rundad).replace('.', ',');
      }
    });

    const notis = this.querySelector('[data-v5-kapad-note]');
    if (notis) notis.hidden = !nagonKapad;
  };

  /** @param {CustomEvent} event */
  uppdatera = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const nyBody = event.detail.data.html.querySelector('product-skardata [ref="skardataBody"]');
    const nuBody = this.querySelector('[ref="skardataBody"]');
    if (!nyBody || !nuBody) return;
    if (nuBody.innerHTML !== nyBody.innerHTML || nuBody.dataset.rpmNative !== nyBody.dataset.rpmNative) {
      nuBody.replaceWith(nyBody);
      this.rakna();
    }
  };
}

if (!customElements.get('product-skardata')) {
  customElements.define('product-skardata', ProductSkardata);
}
